import * as z from 'zod';

import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import type {BrandTagFragment} from '../../brand/query';
import type {ProductFilters} from '../../filters/query';
import {intersectMatchesAndFilters} from '../../filters/utils';
import type {ItemForPriceFragment} from '../../item/query';
import type {ProductAdapter} from '../adapter';
import type {ProductDescription} from '../description';
import type {ProductImageSide} from '../ProductImage';
import type {ProductImageStackDescription} from '../ProductImageStack';
import type {UnionProductCoreFragment} from '../query';

import {DOCUMENT_TYPE_PRODUCT} from './schema/names';
import type {ProductCoreFragment, ProductFragment} from './query';

export function isProduct<P extends UnionProductCoreFragment>(
	product: P,
): product is Extract<P, {_type: typeof DOCUMENT_TYPE_PRODUCT}> {
	return product._type === DOCUMENT_TYPE_PRODUCT;
}

export const productConfigurationSchema = z.object({
	variantId: z.string().nullable(),
});

export type ProductConfiguration = z.infer<typeof productConfigurationSchema>;

export function parseProductConfiguration(value: unknown) {
	const result = productConfigurationSchema.safeParse(value);
	if (!result.success) {
		console.warn('Failed to parse product configuration', result.error);
		return null;
	}

	return result.data;
}

export function getDefaultProductConfiguration(product: ProductCoreFragment): ProductConfiguration {
	const variantId = getFirstVariantKey(product);

	return {
		variantId,
	};
}

export function getFirstVariantKey(product: ProductCoreFragment) {
	if (product.variants?.length) {
		return product.variants[0]._key;
	}

	return null;
}

export function productConfigurationFromFormData(
	product: ProductFragment,
	formData: FormData,
): ProductConfiguration | null {
	// FIXME: Should validate ID here?
	const variantId = formData.get('variantId')?.toString() ?? null;

	return {
		variantId,
	};
}

export function isValidProductConfiguration(
	product: ProductFragment,
	configuration: ProductConfiguration,
) {
	// Check if the variantId is valid:
	const selectedVariant = product.variants?.find(
		(variant) => variant._key === configuration.variantId,
	);
	if (!selectedVariant) {
		return false;
	}

	return true;
}

export function getItemsForProductConfiguration(
	product: ProductCoreFragment,
	configuration: ProductConfiguration,
) {
	const selectedVariant = product.variants?.find((v) => v._key === configuration.variantId);
	const selectedItems: ItemForPriceFragment[] = [];

	if (selectedVariant?.items?.length) {
		selectedItems.push(...selectedVariant.items);
	}

	return selectedItems;
}

export function getProductNameForProductConfiguration(
	product: ProductCoreFragment,
	_configuration: ProductConfiguration,
) {
	return `${product.name}`;
}

export function getDescriptionForProductConfiguration(
	product: ProductCoreFragment,
	configuration: ProductConfiguration,
) {
	const desc: ProductDescription = [];

	const variant = product.variants?.find((variant) => variant._key === configuration.variantId);

	if (!variant || !variant.name) {
		return null;
	} else {
		desc.push({title: 'Variant', value: variant.name});
	}

	return desc;
}

export function getImageStackForProductConfiguration<Side extends ProductImageSide>(
	side: Side,
	product: ProductCoreFragment,
	config: ProductConfiguration | null,
): ProductImageStackDescription | null {
	let selectedVariant = null;
	if (config && product.variants) {
		selectedVariant = product.variants.find((v) => v._key === config.variantId);
	}

	let image = imageFromSanityImage(product.images?.[side]);
	if (!image) {
		// We only consider this side if there is a base image for it:
		return null;
	}

	if (selectedVariant) {
		const img = imageFromSanityImage(selectedVariant.images?.[side]);
		if (img) {
			image = img;
		}
	}

	return {
		image,
		altText: '',
		overlays: [],
	};
}

export const getAllProductBrands = (product: ProductCoreFragment) => {
	const brandsById = new Map<string, BrandTagFragment>();

	// Add the base product brands:
	for (const brand of getBaseProductBrands(product)) {
		brandsById.set(brand._id, brand);
	}

	return Array.from(brandsById.values());
};

export const getConfiguredProductBrands = (
	product: ProductCoreFragment,
	_configuration: ProductConfiguration,
) => {
	const brandsById = new Map<string, BrandTagFragment>();

	// Add the base product brands:
	for (const brand of getBaseProductBrands(product)) {
		brandsById.set(brand._id, brand);
	}

	return Array.from(brandsById.values());
};

const getBaseProductBrands = (product: ProductCoreFragment) => {
	const brandsById = new Map<string, BrandTagFragment>();

	// add the main prduct brand:
	for (const brand of product.brands ?? []) {
		brandsById.set(brand._id, brand);
	}

	return Array.from(brandsById.values());
};

export function getFiltersMatchingProductConfiguration(
	product: ProductCoreFragment,
	filters: ProductFilters,
): ProductFilters | null {
	const matches = {
		brandIds: getAllProductBrands(product).map((b) => b._id),
	} satisfies ProductFilters;

	return intersectMatchesAndFilters(matches, filters);
}

export const productAdapter: ProductAdapter<ProductFragment, ProductCoreFragment> = {
	getName: getProductNameForProductConfiguration,
	getItems: getItemsForProductConfiguration,
	getImages: getImageStackForProductConfiguration,
	getDescription: getDescriptionForProductConfiguration,
	isValidConfig: isValidProductConfiguration,
	getMatchingFilters: getFiltersMatchingProductConfiguration,
	getDefaultConfig: getDefaultProductConfiguration,
	parseConfigFromFormData: productConfigurationFromFormData,
	parseConfigFromJSON: parseProductConfiguration,
};
